import { useState } from 'react';
import { MapPin, Star, Filter, Search, Lock, Unlock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import LockedContentCard from '../components/LockedContentCard';

const allProperties = [
  {
    id: '1',
    name: 'Serene Valley Resort',
    location: 'Coorg',
    state: 'Karnataka',
    type: 'Eco-Lodge',
    price: 3500,
    image: 'https://images.unsplash.com/photo-1672401231510-1365539ffc81',
    description: 'Nestled in coffee plantations with stunning valley views',
    rating: 4.8,
    email: 'contact@serenevalley.com',
    phone: '+91 9876543210'
  },
  {
    id: '2',
    name: 'Royal Heritage Haveli',
    location: 'Jaipur',
    state: 'Rajasthan',
    type: 'Boutique Hotel',
    price: 5500,
    image: 'https://images.unsplash.com/photo-1694267900696-0d8b1cff6728',
    description: 'Traditional Rajasthani architecture with modern amenities',
    rating: 4.9,
    email: 'info@royalhaveli.com',
    phone: '+91 9876543211'
  },
  {
    id: '3',
    name: 'Coastal Paradise Resort',
    location: 'Goa',
    state: 'Goa',
    type: 'Beach Resort',
    price: 6000,
    image: 'https://images.unsplash.com/photo-1685423705981-f7149db95a86',
    description: 'Private beach access with infinity pool',
    rating: 4.7,
    email: 'bookings@coastalparadise.com',
    phone: '+91 9876543212'
  },
  {
    id: '4',
    name: 'Wilderness Lodge',
    location: 'Ranthambore',
    state: 'Rajasthan',
    type: 'Wildlife Resort',
    price: 7500,
    image: 'https://images.unsplash.com/photo-1649731000184-7ced04998f44',
    description: 'Experience wildlife safaris from this luxury lodge',
    rating: 4.9,
    email: 'reservations@wildernesslodge.com',
    phone: '+91 9876543213'
  },
  {
    id: '5',
    name: 'Mountain Mist Homestay',
    location: 'Manali',
    state: 'Himachal Pradesh',
    type: 'Hill Station',
    price: 2500,
    image: 'https://images.unsplash.com/photo-1656533391146-dad89b5a8b40',
    description: 'Cozy mountain retreat with Himalayan views',
    rating: 4.6,
    email: 'stay@mountainmist.com',
    phone: '+91 9876543214'
  },
  {
    id: '6',
    name: 'Jungle Treehouse Retreat',
    location: 'Wayanad',
    state: 'Kerala',
    type: 'Experiential Stay',
    price: 4000,
    image: 'https://images.unsplash.com/photo-1671739961405-add4f0ece0c3',
    description: 'Unique treehouse experience in tropical forests',
    rating: 4.8,
    email: 'info@jungletreehouse.com',
    phone: '+91 9876543215'
  },
  {
    id: '7',
    name: 'Lake View Resort',
    location: 'Udaipur',
    state: 'Rajasthan',
    type: 'Boutique Hotel',
    price: 6500,
    image: 'https://images.unsplash.com/photo-1683914791874-2dcb78e58e09',
    description: 'Stunning lake views with royal hospitality',
    rating: 4.7,
    email: 'reservations@lakeview.com',
    phone: '+91 9876543216'
  },
  {
    id: '8',
    name: 'Backwater Bliss',
    location: 'Alleppey',
    state: 'Kerala',
    type: 'Eco-Lodge',
    price: 3800,
    image: 'https://images.unsplash.com/photo-1445019980597-93fa8acb246c',
    description: 'Peaceful backwater stay with houseboat access',
    rating: 4.8,
    email: 'contact@backwaterbliss.com',
    phone: '+91 9876543217'
  },
  {
    id: '9',
    name: 'Desert Dunes Camp',
    location: 'Jaisalmer',
    state: 'Rajasthan',
    type: 'Experiential Stay',
    price: 4500,
    image: 'https://images.pexels.com/photos/4870581/pexels-photo-4870581.jpeg',
    description: 'Luxury camping in the Thar Desert',
    rating: 4.6,
    email: 'bookings@desertdunes.com',
    phone: '+91 9876543218'
  },
  {
    id: '10',
    name: 'Tea Estate Bungalow',
    location: 'Munnar',
    state: 'Kerala',
    type: 'Hill Station',
    price: 3200,
    image: 'https://images.pexels.com/photos/27382731/pexels-photo-27382731.jpeg',
    description: 'Colonial bungalow in tea plantations',
    rating: 4.7,
    email: 'stay@teaestate.com',
    phone: '+91 9876543219'
  },
  {
    id: '11',
    name: 'Himalayan Retreat',
    location: 'Shimla',
    state: 'Himachal Pradesh',
    type: 'Hill Station',
    price: 2800,
    image: 'https://images.unsplash.com/photo-1671739961396-d956a74a3ffe',
    description: 'Mountain lodge with pine forest surroundings',
    rating: 4.5,
    email: 'info@himalayanretreat.com',
    phone: '+91 9876543220'
  },
  {
    id: '12',
    name: 'Beach Cottage Goa',
    location: 'Goa',
    state: 'Goa',
    type: 'Beach Resort',
    price: 4200,
    image: 'https://images.unsplash.com/photo-1759150594951-f251de68986b',
    description: 'Charming cottages steps from the beach',
    rating: 4.8,
    email: 'bookings@beachcottage.com',
    phone: '+91 9876543221'
  }
];

function PropertiesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedState, setSelectedState] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [priceRange, setPriceRange] = useState('All');
  const [showFilters, setShowFilters] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false); // Demo toggle for locked/unlocked view
  
  // Demo current user - in a real app, this would come from authentication
  const currentUser = {
    id: 'demo-user-1',
    name: 'Demo Resort',
    email: 'demo@travelodges.com',
    phone: '+91 9999999999',
    subscriptionPlan: 'Pro' // Basic, Pro, or Premium
  };

  const states = ['All', ...new Set(allProperties.map(p => p.state))];
  const types = ['All', ...new Set(allProperties.map(p => p.type))];
  const priceRanges = [
    { label: 'All', min: 0, max: Infinity },
    { label: 'Under ₹3,000', min: 0, max: 3000 },
    { label: '₹3,000 - ₹5,000', min: 3000, max: 5000 },
    { label: '₹5,000 - ₹7,000', min: 5000, max: 7000 },
    { label: 'Above ₹7,000', min: 7000, max: Infinity }
  ];

  const filteredProperties = allProperties.filter(property => {
    const matchesSearch = property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesState = selectedState === 'All' || property.state === selectedState;
    const matchesType = selectedType === 'All' || property.type === selectedType;
    
    const selectedPriceRange = priceRanges.find(r => r.label === priceRange);
    const matchesPrice = priceRange === 'All' || 
                        (property.price >= selectedPriceRange.min && property.price <= selectedPriceRange.max);
    
    return matchesSearch && matchesState && matchesType && matchesPrice;
  });

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Hero Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-b" data-testid="properties-hero">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Discover Partner Resorts</h1>
          <p className="text-xl text-gray-600 mb-6">Explore our network of verified independent resorts across India</p>
          
          {/* Search Bar */}
          <div className="flex gap-4 max-w-3xl">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input 
                type="text"
                placeholder="Search by name or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              data-testid="filter-toggle"
            >
              <Filter className="h-5 w-5 mr-2" />
              Filters
            </Button>
            <Button 
              variant={isSubscribed ? "default" : "outline"}
              onClick={() => setIsSubscribed(!isSubscribed)}
              data-testid="subscription-toggle"
              className={isSubscribed ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            >
              {isSubscribed ? <Unlock className="h-5 w-5 mr-2" /> : <Lock className="h-5 w-5 mr-2" />}
              {isSubscribed ? 'Subscribed' : 'Non-Subscriber'}
            </Button>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      {showFilters && (
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-white border-b" data-testid="filters-section">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State/City</label>
                <select 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  data-testid="state-filter"
                >
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
                <select 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  data-testid="type-filter"
                >
                  {types.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
                <select 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  data-testid="price-filter"
                >
                  {priceRanges.map(range => (
                    <option key={range.label} value={range.label}>{range.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Properties Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8" data-testid="properties-grid">
        <div className="max-w-7xl mx-auto">
          {/* Info Banner */}
          {!isSubscribed && (
            <Card className="mb-8 bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200" data-testid="lock-info-banner">
              <CardContent className="py-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Lock className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">🔒 Premium Content Locked</h3>
                    <p className="text-gray-700 mb-3">
                      This feature is part of the Travelodges private network. Full access to resort names, profiles, referral tools, analytics, and downloads is available only to subscribed members.
                    </p>
                    <p className="text-gray-700 mb-4">
                      Subscribe now to unlock all features, connect directly with other resorts, and grow your bookings without OTA commissions.
                    </p>
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => window.location.href = '/pricing'}
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      Unlock Full Access
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <div className="mb-6">
            <p className="text-gray-600">
              Showing <span className="font-semibold">{filteredProperties.length}</span> properties
              {isSubscribed && <Badge className="ml-2 bg-emerald-100 text-emerald-800">✅ Full Access</Badge>}
              {!isSubscribed && <Badge className="ml-2 bg-gray-200 text-gray-700">🔒 Limited View</Badge>}
            </p>
          </div>

          {filteredProperties.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <p className="text-gray-600 text-lg">No properties found matching your filters. Try adjusting your search criteria.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProperties.map((property) => (
                <LockedContentCard 
                  key={property.id} 
                  property={property} 
                  isSubscribed={isSubscribed}
                  currentUser={currentUser}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default PropertiesPage;